import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken, getLegacyUrl } from "./api";

import ProcessorViewShell from "./apbot/processorpages/ProcessorViewShell";

import { openInvoiceData } from "./utils/invoiceDisplay";
import { openInvoicePdf } from "./utils/openPdf";

function formatDate(value) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
}

function getPendingDuration(createdAt) {
  if (!createdAt) {
    return "-";
  }

  const createdTime = new Date(createdAt);
  const now = new Date();
  let diffMs = now - createdTime;
  if (diffMs < 0) {
    diffMs = 0;
  }

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}

export default function UploaderInvoiceStatusView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const summary = useMemo(
    () => ({
      count: rows.length,
      failed: rows.filter((row) => row.status === "Failed").length,
      completed: rows.filter((row) => row.status && row.status !== "Failed").length,
    }),
    [rows],
  );

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/invoice-upload-status/", {
        credentials: "include",
      });
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.message || "Unable to load invoice status.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load invoice status.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  async function handleAction(invoiceId, type) {
    setStatus("");
    setError("");
    setActionKey(`${type}-${invoiceId}`);

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch(type === "resubmit" ? "/invoice/resubmit/" : "/invoice/reject/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ invoice_id: invoiceId }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || `Unable to ${type} invoice.`);
      }

      setStatus(payload.message || `Invoice ${type}ed successfully.`);
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || `Unable to ${type} invoice.`);
    } finally {
      setActionKey("");
    }
  }

  function openLegacyInvoiceStatusPage() {
    window.localStorage.setItem("activeSection", "invoices");
    window.open(getLegacyUrl("/uploader_dashboard/"), "_blank", "noreferrer");
  }

  function refreshPendingActions() {
    loadRows();
  }

  return (
    <ProcessorViewShell
      title="Invoice Status"
      intro=""
      metrics={[
        { label: "Uploaded Invoices", value: summary.count },
        { label: "Failed", value: summary.failed },
        { label: "Success", value: summary.completed },
      ]}
      actions={
        <button
          className="ghost-button processor-view__refresh"
          type="button"
          onClick={refreshPendingActions}
        >
          Refresh
        </button>
      }
    >
      <section className="uploader-view">
        <div className="workspace-panel">
          

          {loading ? (
            <p className="config-note">Loading invoice status…</p>
          ) : (
            <div className="config-table processor-table">
              <table>
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Invoice No</th>
                    <th>Vendor</th>
                    <th>Invoice Date</th>
                    <th>Vendor GST</th>
                    <th>PDF</th>
                    <th>Age</th>
                    <th>Status</th>
                    <th>Resubmit</th>
                    <th>Reject</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length ? (
                    rows.map((row) => {
                      const disabled = row.status !== "Failed";
                      return (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.InvoiceNo || "-"}</td>
                          <td>{row.VendorName || "-"}</td>
                          <td>{formatDate(row.InvoiceDate)}</td>
                          <td>{row.VendorGst || "-"}</td>
                          <td>
                            {row.unique_name ? (
                              <button
                                type="button"
                                onClick={() => openInvoicePdf(row.id)}
                              >
                                📎
                              </button>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>{getPendingDuration(row.UpdatedAt)}</td>
                          <td>{row.status || "-"}</td>
                          <td>
                            <button
                              className="ghost-button"
                              type="button"
                              disabled={disabled || actionKey === `resubmit-${row.id}`}
                              onClick={() => handleAction(row.id, "resubmit")}
                            >
                              {actionKey === `resubmit-${row.id}` ? "Resubmitting..." : "Resubmit"}
                            </button>
                          </td>
                          <td>
                            <button
                              className="ghost-button"
                              type="button"
                              disabled={disabled || actionKey === `reject-${row.id}`}
                              onClick={() => handleAction(row.id, "reject")}
                            >
                              {actionKey === `reject-${row.id}` ? "Rejecting..." : "Reject"}
                            </button>
                          </td>
                          <td>{row.reason || "-"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10">No invoices found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </ProcessorViewShell>
  );
}
